/**
 * VietClinics multilingual system.
 *
 * Architecture:
 *  - 9 locales (en, vi, fr, de, ar, zh, ko, ja, ru) stored as JSON in `/locales/`.
 *  - `t('namespace.key')` deep lookup with English fallback, then raw key fallback.
 *  - Selected locale persisted in localStorage; first visit auto-detects from
 *    `navigator.language` against supported list, falling back to English.
 *  - Arabic (ar) flips `document.dir` to `rtl` and adds `rtl` class on <html>.
 *  - Updates <html lang> on every change for accessibility + SEO.
 *
 * URL locale prefix routing lives in `LocaleRouter` (see App.js) so the same
 * components serve any of the 9 locales via the same route table.
 */

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

import en from '../locales/en.json';
import vi from '../locales/vi.json';
import fr from '../locales/fr.json';
import de from '../locales/de.json';
import ar from '../locales/ar.json';
import zh from '../locales/zh.json';
import ko from '../locales/ko.json';
import ja from '../locales/ja.json';
import ru from '../locales/ru.json';

const DICT = { en, vi, fr, de, ar, zh, ko, ja, ru };

export const LANGUAGES = [
  { code: 'en', name: 'English',    nativeName: 'English',     flag: '🇬🇧', dir: 'ltr' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt',  flag: '🇻🇳', dir: 'ltr' },
  { code: 'fr', name: 'French',     nativeName: 'Français',    flag: '🇫🇷', dir: 'ltr' },
  { code: 'de', name: 'German',     nativeName: 'Deutsch',     flag: '🇩🇪', dir: 'ltr' },
  { code: 'ar', name: 'Arabic',     nativeName: 'العربية',     flag: '🇦🇪', dir: 'rtl' },
  { code: 'zh', name: 'Chinese',    nativeName: '中文',          flag: '🇨🇳', dir: 'ltr' },
  { code: 'ko', name: 'Korean',     nativeName: '한국어',         flag: '🇰🇷', dir: 'ltr' },
  { code: 'ja', name: 'Japanese',   nativeName: '日本語',         flag: '🇯🇵', dir: 'ltr' },
  { code: 'ru', name: 'Russian',    nativeName: 'Русский',     flag: '🇷🇺', dir: 'ltr' },
];

export const SUPPORTED_CODES = LANGUAGES.map((l) => l.code);

const STORAGE_KEY = 'vietclinics_language';

/* Deep key lookup: t('home.hero.title') traverses nested object */
function deepGet(obj, key) {
  if (!obj || typeof key !== 'string') return undefined;
  const parts = key.split('.');
  let cur = obj;
  for (const p of parts) {
    if (cur && Object.prototype.hasOwnProperty.call(cur, p)) cur = cur[p];
    else return undefined;
  }
  return typeof cur === 'string' ? cur : undefined;
}

function detectInitialLanguage() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED_CODES.includes(saved)) return saved;
  } catch { /* localStorage may be blocked */ }
  if (typeof navigator !== 'undefined' && navigator.language) {
    const short = navigator.language.toLowerCase().split('-')[0];
    if (SUPPORTED_CODES.includes(short)) return short;
  }
  return 'en';
}

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children, initialLanguage }) => {
  const [language, setLanguage] = useState(() => initialLanguage || detectInitialLanguage());

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, language); } catch { /* ignore */ }
    const meta = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];
    document.documentElement.lang = language;
    document.documentElement.dir = meta.dir;
    document.documentElement.classList.toggle('rtl', meta.dir === 'rtl');
  }, [language]);

  /* External locale changes (router) re-sync via setLanguage */
  const changeLanguage = (code) => {
    if (SUPPORTED_CODES.includes(code) && code !== language) setLanguage(code);
  };

  const t = useMemo(() => {
    return (key, vars) => {
      let value = deepGet(DICT[language], key) ?? deepGet(DICT.en, key) ?? key;
      if (vars && typeof value === 'string') {
        for (const [k, v] of Object.entries(vars)) {
          value = value.replaceAll(`{${k}}`, String(v));
        }
      }
      return value;
    };
  }, [language]);

  const currentLanguage = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];

  return (
    <LanguageContext.Provider value={{ language, t, changeLanguage, currentLanguage, LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider');
  return ctx;
};

/* Convenience hook mirroring react-i18next API */
export const useT = () => {
  const { t } = useLanguage();
  return t;
};

export default LanguageContext;
